import { useEffect, useState } from 'react';
import AuthenticatedLayout from '../layouts/AuthenticatedLayout';
import { TrashIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import Swal from 'sweetalert2';

export default function SignedAgreements() {
    const [user, setUser] = useState(null);
    const [agreements, setAgreements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [sortKey, setSortKey] = useState("signed_at");
    const [sortOrder, setSortOrder] = useState("desc");
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(12);
    // Filtering, sorting, and pagination logic
    const filteredAgreements = agreements.filter((ag) =>
        (ag.signed_by?.toLowerCase().includes(searchQuery.toLowerCase()) ||
         ag.oib?.toLowerCase().includes(searchQuery.toLowerCase()) ||
         ag.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
         ag.address?.toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const sortedAgreements = [...filteredAgreements].sort((a, b) => {
        let aValue = a[sortKey] || "";
        let bValue = b[sortKey] || "";
        if (sortKey === "signed_at") {
            aValue = new Date(aValue);
            bValue = new Date(bValue);
        }
        if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
        if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
        return 0;
    });

    const totalPages = Math.ceil(sortedAgreements.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = sortedAgreements.slice(indexOfFirstItem, indexOfLastItem);
    const handleSort = (key) => {
        if (sortKey === key) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortKey(key);
            setSortOrder("asc");
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    const handlePrevPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    useEffect(() => {
        const userData = localStorage.getItem('wp_user');
        if (userData) setUser(JSON.parse(userData));
        fetchAgreements();
    }, []);

    const fetchAgreements = () => {
        const token = localStorage.getItem('wp_jwt');
        if (!token) return;
        const apiUrl = process.env.REACT_APP_WORDPRESS_URL;
        setLoading(true);
        fetch(`${apiUrl}/wp-json/biznis/v1/signed-agreements`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
            .then((res) => res.json())
            .then((data) => {
                setAgreements(data.data);
                setLoading(false);
            })
            .catch((err) => {
                console.error('Error fetching signed agreements:', err);
                setLoading(false);
            });
    };

    const deleteAgreement = (id) => {
        Swal.fire({
            title: 'Jeste li sigurni?',
            text: 'Ovaj ugovor će biti trajno izbrisan.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Da, izbriši!',
            cancelButtonText: 'Otkaži',
        }).then((result) => {
            if (result.isConfirmed) {
                const token = localStorage.getItem('wp_jwt');
                const apiUrl = process.env.REACT_APP_WORDPRESS_URL;
                fetch(`${apiUrl}/wp-json/biznis/v1/signed-agreements/${id}`, {
                    method: 'DELETE',
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                })
                    .then((res) => res.json())
                    .then(() => {
                        setAgreements(prev => prev.filter(ag => ag.id !== id));
                        Swal.fire('Izbrisano!', 'Ugovor je uspješno izbrisan.', 'success');
                    })
                    .catch((err) => {
                        console.error(err);
                        Swal.fire('Greška', 'Brisanje nije uspjelo.', 'error');
                    });
            }
        });
    };

    const downloadFile = (fileUrl) => {
        const link = document.createElement('a');
        link.href = fileUrl;
        link.setAttribute('download', '');
        link.setAttribute('target', '_blank');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <AuthenticatedLayout
            user={user}
            header={<h2 className="font-semibold text-xl text-white leading-tight">Potpisani Ugovori</h2>}
        >
            <div className="sm:mt-4 mx-auto sm:px-6 lg:px-8">
                <div className="bg-white shadow-sm sm:rounded-lg p-3 sm:p-8">
                    {/* Search and controls */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
                        <input
                            type="text"
                            placeholder="Pretraži ugovore..."
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full sm:w-1/2"
                        />
                        <div className="flex flex-col sm:flex-row sm:items-center justify-end gap-2 sm:w-1/2">
                            <select
                                value={itemsPerPage}
                                onChange={(e) => {
                                    setItemsPerPage(Number(e.target.value));
                                    setCurrentPage(1);
                                }}
                                className="border border-gray-300 rounded px-3 rounded-lg py-2 text-sm"
                            >
                                {[8, 12, 20, 50, 100].map(n => (
                                    <option key={n} value={n}>{n} po stranici</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    {loading ? (
                        <div className="flex flex-col w-full items-center">
                            <div className="animate-spin border-2 border-t-2 border-blue-950 w-5 h-5 rounded-full"></div>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            {sortedAgreements.length === 0 ? (
                                <p className="text-gray-500">Nema potpisanih ugovora.</p>
                            ) : (
                                <table className="min-w-full divide-y divide-gray-200 border border-gray-300">
                                    <thead className="bg-gray-100">
                                    <tr>
                                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 cursor-pointer" onClick={() => handleSort('signed_by')}>
                                            Ime i Prezime {sortKey === 'signed_by' && (sortOrder === 'asc' ? '▲' : '▼')}
                                        </th>
                                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 cursor-pointer" onClick={() => handleSort('oib')}>
                                            OIB {sortKey === 'oib' && (sortOrder === 'asc' ? '▲' : '▼')}
                                        </th>
                                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 cursor-pointer" onClick={() => handleSort('email')}>
                                            Email Adresa {sortKey === 'email' && (sortOrder === 'asc' ? '▲' : '▼')}
                                        </th>
                                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 cursor-pointer" onClick={() => handleSort('address')}>
                                            Adresa {sortKey === 'address' && (sortOrder === 'asc' ? '▲' : '▼')}
                                        </th>
                                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 cursor-pointer" onClick={() => handleSort('signed_at')}>
                                            Datum {sortKey === 'signed_at' && (sortOrder === 'asc' ? '▲' : '▼')}
                                        </th>
                                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Akcije</th>
                                    </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                    {currentItems.map((ag) => (
                                        <tr key={ag.id} className="hover:bg-gray-200">
                                            <td className="px-4 py-2 text-sm text-gray-800">{ag.signed_by}</td>
                                            <td className="px-4 py-2 text-sm text-gray-800">{ag.oib}</td>
                                            <td className="px-4 py-2 text-sm text-gray-800">{ag.email}</td>
                                            <td className="px-4 py-2 text-sm text-gray-800">{ag.address}</td>
                                            <td className="px-4 py-2 text-sm text-gray-600">{ag.signed_at}</td>
                                            <td className="px-4 py-2 text-sm text-gray-600 flex gap-2">
                                                <button
                                                    onClick={() => downloadFile(ag.file_url)}
                                                    className="text-blue-950 hover:text-blue-800 cursor-pointer"
                                                    title="Preuzmi PDF"
                                                >
                                                    <ArrowDownTrayIcon className="w-5 h-5"/>
                                                </button>
                                                <button
                                                    onClick={() => deleteAgreement(ag.id)}
                                                    className="text-red-600 hover:text-red-800 cursor-pointer"
                                                    title="Izbriši"
                                                >
                                                    <TrashIcon className="w-5 h-5"/>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            )}
                            {/* Pagination */}
                            {sortedAgreements.length > 0 && (
                                <div className="flex justify-between items-center mt-6">
                                    <button
                                        onClick={handlePrevPage}
                                        disabled={currentPage === 1}
                                        className="px-4 py-2 bg-blue-950 text-white rounded disabled:opacity-50"
                                    >
                                        Prev
                                    </button>
                                    <span className="mx-4 text-sm">
                                        Page {currentPage} of {totalPages}
                                    </span>
                                    <button
                                        onClick={handleNextPage}
                                        disabled={currentPage === totalPages}
                                        className="px-4 py-2 bg-blue-950 text-white rounded disabled:opacity-50"
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </AuthenticatedLayout>
    );
}
